import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Bin, histogram, max, min } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { Size } from '../core/size.service';

const PAD_PROPORTION = 0.05;
const MIN_TICKS = 1;
const MAX_TICKS = 10;

@Component({
  selector: 'bar-histogram',
  templateUrl: './bar-histogram.component.html',
  styleUrls: ['./bar-histogram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarHistogramComponent<T> {
  svgSize: Size = { width: 1, height: 1 };
  readonly LEFT_OFFSET = 32;
  readonly BOTTOM_OFFSET = 20;

  @Input()
  data: T[] = [];

  @Input()
  calculateBin: (datum: T) => number = () => 1;

  @Input()
  calculateValue: (data: T[]) => number = data => (data ? data.length : 1);

  @Input()
  binFormatter: (bin: number) => string = bin => bin.toString();

  @Input()
  valueFormatter: (value: number) => string = value => value.toString();

  @Input()
  binCount: number = 7;

  @Input()
  highlightRange: [number, number] | null = null;

  @Input()
  highlightRangeTooltip: string;

  @Input()
  min: number | null;

  @Input()
  max: number | null;

  getXScale() {
    const binValues = this.data.map(this.calculateBin);
    const minValue = this.min ?? (min(binValues) || 0);
    const maxValue = this.max ?? (max(binValues) || 0);
    const range = maxValue - minValue;
    const padding = (range * PAD_PROPORTION) / 2;
    return scaleLinear()
      .domain([minValue - padding, maxValue + padding])
      .range([this.LEFT_OFFSET, this.svgSize.width]);
  }

  getXTicks() {
    const scale = this.getXScale();
    const [minValue, maxValue] = scale.domain();
    return scale.ticks(
      Math.max(Math.min(Math.round(maxValue - minValue), MAX_TICKS), MIN_TICKS),
    );
  }

  getHistogram(): Bin<T, number>[] {
    const domain = this.getXScale().domain() as [number, number];
    return histogram<T, number>()
      .value(this.calculateBin)
      .domain(() => domain)
      .thresholds((_, min, max) => {
        const step = (max - min) / this.binCount;
        return Array(this.binCount - 1)
          .fill(null)
          .map((_, i) => min + step * (i + 1));
      })(this.data);
  }

  getYScale() {
    const bins = this.getHistogram();
    return scaleLinear()
      .domain([0, max(bins, d => this.calculateValue(d)) || 0])
      .range([this.svgSize.height - this.BOTTOM_OFFSET, 0]);
  }

  getYTicks() {
    return this.getYScale().ticks(6);
  }

  getBars() {
    const bins = this.getHistogram();
    const xScale = this.getXScale();
    const yScale = this.getYScale();
    return bins.map(bin => {
      const value = this.calculateValue(bin);
      return {
        value,
        binLowerBound: bin.x0,
        binUpperBound: bin.x1,
        x: xScale(bin.x0!)!,
        y: yScale(value)!,
        w: Math.max((xScale(bin.x1!)! - xScale(bin.x0!)!) * 0.97, 0),
        h: Math.max(yScale(0)! - yScale(value)!, 0),
      };
    });
  }

  getHighlightRect() {
    if (!this.highlightRange) return null;
    const [start, end] = this.highlightRange;
    const xScale = this.getXScale();
    const yScale = this.getYScale();
    return {
      x: xScale(start),
      y: 0,
      width: xScale(end)! - xScale(start)!,
      height: yScale(0),
    };
  }

  minZero(n: number | undefined): number {
    return Math.max(n ?? 0, 0);
  }
}
